import { localization } from '@bb/contracts';
import type { EnvironmentSummary, TranslationStatus } from '../../../api/client.ts';

/**
 * Pure helpers behind message localization in the dashboard (BB-55). They mirror `@bb/domain`
 * (`content/locale-resolve.ts`, `content/localized-content.ts`), which the dashboard does not depend on: what the
 * preview shows must be what a send renders. A channel's content keeps the default-locale fields as they are and
 * adds `localized: { [locale]: partial content }` and `translationStatus: { [locale]: status }`.
 */
export type ChannelContent = Record<string, unknown>;
export type { TranslationStatus };
export const TRANSLATION_STATUSES = localization.TRANSLATION_STATUSES;

export interface LocaleSettings {
  defaultLocale: string;
  locales?: ReadonlyArray<{ code: string; default?: boolean; fallback?: string }>;
}

type Localized = Record<string, Record<string, unknown>>;
type Statuses = Partial<Record<string, TranslationStatus>>;

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A language tag as written by apps and imports (`PT_br`, `pt-br`) in canonical form (`pt-BR`); `null` if it is not one. */
export function normalizeLocale(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    return Intl.getCanonicalLocales(value.trim().replace(/_/g, '-'))[0] ?? null;
  } catch {
    return null;
  }
}

const languageOf = (locale: string) => locale.split('-')[0]!.toLowerCase();

/** The locales of an environment (`settings.locales`, else only `defaultLocale`), default first. */
export function localeSettingsOf(environment: Pick<EnvironmentSummary, 'defaultLocale' | 'settings'> | undefined): LocaleSettings {
  const locales = environment?.settings?.locales;
  return { defaultLocale: environment?.defaultLocale ?? 'en', ...(locales?.length ? { locales } : {}) };
}

/** The environment's locale codes with the default first, and the fallback of each. */
export function environmentLocales(settings: LocaleSettings): { defaultLocale: string; codes: string[]; fallback: Map<string, string> } {
  const list = settings.locales?.length ? settings.locales : [{ code: settings.defaultLocale, default: true }];
  const norm = (code: string) => normalizeLocale(code) ?? code;
  const defaultLocale = norm(list.find((l) => l.default)?.code ?? settings.defaultLocale);
  const others = list.map((l) => norm(l.code)).filter((c) => c !== defaultLocale);
  const fallback = new Map(list.filter((l) => l.fallback).map((l) => [norm(l.code), norm(l.fallback!)]));
  return { defaultLocale, codes: [defaultLocale, ...new Set(others)], fallback };
}

/** Where content of a locale comes from when it has no translation: its fallbacks in order, then the default. */
export function localeChain(locale: string, settings: LocaleSettings): string[] {
  const { fallback, defaultLocale } = environmentLocales(settings);
  const chain: string[] = [];
  for (let next: string | undefined = locale; next && !chain.includes(next); next = fallback.get(next)) chain.push(next);
  if (!chain.includes(defaultLocale)) chain.push(defaultLocale);
  return chain;
}

/** `over` laid on `base`: objects merge key by key, anything else (arrays included) replaces. */
function deepMerge(base: Record<string, unknown>, over: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(over)) out[key] = isPlainObject(value) && isPlainObject(out[key]) ? deepMerge(out[key] as Record<string, unknown>, value) : value;
  return out;
}

/** Content without `localized` / `translationStatus`: the default locale. */
export function defaultContent(content: ChannelContent): ChannelContent {
  const { localized: _localized, translationStatus: _status, ...base } = content;
  return base;
}

/**
 * The content of a channel in a locale, as a send renders it: the first locale of `chain` that has a translation
 * (not `missing`) laid over the default fields; `locale` is the one whose translation was used (the default if none).
 */
export function localizedContent(content: ChannelContent, chain: readonly string[]): { content: ChannelContent; locale: string } {
  const localized = content.localized as Localized | undefined;
  const status = content.translationStatus as Statuses | undefined;
  const base = defaultContent(content);
  for (const locale of chain.slice(0, -1)) {
    const translation = localized?.[locale];
    if (translation && status?.[locale] !== 'missing') return { content: deepMerge(base, translation), locale };
  }
  return { content: base, locale: chain.at(-1) ?? '' };
}

/** What a profile of `locale` sees: the content through the locale's fallback chain. */
export const previewContent = (content: ChannelContent, locale: string, settings: LocaleSettings) => localizedContent(content, localeChain(locale, settings));

/** Links and ids are strings too, but nothing to translate. */
const isTranslatable = (key: string, value: string) => !/(url|uri|link|id)$/i.test(key) && !/^[a-z][a-z0-9+.-]*:\/\//i.test(value) && value.trim() !== '';

/** Text fields of the default content, as dot paths (`card.title`); what a translation has to cover. */
export function textFieldPaths(content: ChannelContent, prefix = ''): string[] {
  const out: string[] = [];
  for (const [key, value] of Object.entries(content)) {
    if (key === 'localized' || key === 'translationStatus') continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string' && isTranslatable(key, value)) out.push(path);
    else if (isPlainObject(value)) out.push(...textFieldPaths(value, path));
  }
  return out;
}

export const readPath = (object: Record<string, unknown>, path: string): unknown => path.split('.').reduce<unknown>((v, k) => (isPlainObject(v) ? v[k] : undefined), object);

export function writePath(object: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...object, [head!]: value };
  const child = isPlainObject(object[head!]) ? (object[head!] as Record<string, unknown>) : {};
  return { ...object, [head!]: writePath(child, rest.join('.'), value) };
}

/** `object` without the field at `path`; objects left empty go too. */
export function removePath(object: Record<string, unknown>, path: string): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  const { [head!]: current, ...others } = object;
  if (!rest.length || !isPlainObject(current)) return others;
  const child = removePath(current, rest.join('.'));
  return Object.keys(child).length ? { ...others, [head!]: child } : others;
}

const textAt = (object: Record<string, unknown> | undefined, path: string) => {
  const v = readPath(object ?? {}, path);
  return typeof v === 'string' ? v : '';
};

/** The translated text of a field in a locale; empty when the locale has none (it falls back). */
export const translationOf = (content: ChannelContent, locale: string, path: string) => textAt((content.localized as Localized | undefined)?.[locale], path);

/** A locale's status: as set, else `draft` when it has a translation and `missing` when it has none (as a send reads it). */
export function translationStatusOf(content: ChannelContent, locale: string): TranslationStatus {
  const set = (content.translationStatus as Statuses | undefined)?.[locale];
  if (set) return set;
  const translation = (content.localized as Localized | undefined)?.[locale];
  return translation && Object.keys(translation).length ? 'draft' : 'missing';
}

/** Text fields a locale's translation leaves empty. */
export const emptyFields = (content: ChannelContent, locale: string, fields: readonly string[] = textFieldPaths(content)) => fields.filter((f) => !translationOf(content, locale, f).trim());

/** Locales marked `ready` whose translation leaves a text field empty: the server refuses to publish them. */
export function incompleteReadyLocales(content: ChannelContent, fields: readonly string[] = textFieldPaths(content)): Array<{ locale: string; missing: string[] }> {
  return Object.entries((content.translationStatus as Statuses | undefined) ?? {})
    .filter(([, status]) => status === 'ready')
    .map(([locale]) => ({ locale, missing: emptyFields(content, locale, fields) }))
    .filter((r) => r.missing.length > 0);
}

/** The content with `localized` and `translationStatus` replaced; empty ones are left out. */
export function withTranslations(content: ChannelContent, localized: Localized, status: Statuses): ChannelContent {
  const base = defaultContent(content);
  return { ...base, ...(Object.keys(localized).length ? { localized } : {}), ...(Object.keys(status).length ? { translationStatus: status } : {}) };
}

/** Sets (or, when empty, clears) a field of a locale's translation; typing into an untranslated locale makes it a `draft`. */
export function setTranslation(content: ChannelContent, locale: string, path: string, text: string): ChannelContent {
  const localized: Localized = { ...((content.localized as Localized | undefined) ?? {}) };
  const status: Statuses = { ...((content.translationStatus as Statuses | undefined) ?? {}) };
  const current = localized[locale] ?? {};
  const next = text === '' ? removePath(current, path) : writePath(current, path, text);
  if (Object.keys(next).length) localized[locale] = next;
  else delete localized[locale];
  // A `missing` translation is not sent: what the editor types must reach users once saved.
  if (text !== '' && (status[locale] ?? 'missing') === 'missing') status[locale] = 'draft';
  return withTranslations(content, localized, status);
}

export function setTranslationStatus(content: ChannelContent, locale: string, next: TranslationStatus): ChannelContent {
  return withTranslations(content, { ...((content.localized as Localized | undefined) ?? {}) }, { ...((content.translationStatus as Statuses | undefined) ?? {}), [locale]: next });
}

export const isRtlLocale = (locale: string): boolean => (localization.RTL_LANGUAGES as readonly string[]).includes(languageOf(locale));

/** `card.title` → «Card title», `linkText` → «Link text». */
export function humanizePath(path: string): string {
  const words = path
    .split('.')
    .join(' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
